import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BookOpen, ArrowRight, Bot, Package, HelpCircle } from 'lucide-react';

const HelpCenter = () => {
  const { t } = useTranslation();

  const guides = [
    {
      icon: Bot,
      title: t('help.chatbot.title'),
      description: t('help.chatbot.subtitle'),
      href: '/portal/help/chatbot',
      links: [
        { label: t('help.chatbot.toc_create'),    href: '/portal/help/chatbot#create' },
        { label: t('help.chatbot.toc_kb'),         href: '/portal/help/chatbot#knowledge-base' },
        { label: t('help.chatbot.toc_website'),    href: '/portal/help/chatbot#website' },
        { label: t('help.chatbot.toc_mobile'),     href: '/portal/help/chatbot#mobile' },
        { label: t('help.chatbot.toc_api'),        href: '/portal/help/chatbot#api' },
        { label: t('help.chatbot.toc_whatsapp'),   href: '/portal/help/chatbot#whatsapp' },
        { label: t('help.chatbot.toc_messenger'),  href: '/portal/help/chatbot#messenger' },
        { label: t('help.chatbot.toc_instagram'),  href: '/portal/help/chatbot#instagram' },
      ],
    },
    {
      icon: Package,
      title: t('help.cmms.title'),
      description: t('help.cmms.subtitle'),
      href: '/portal/help/cmms',
      links: [
        { label: t('help.cmms.toc_start'),  href: '/portal/help/cmms#getting-started' },
        { label: t('help.cmms.toc_assets'), href: '/portal/help/cmms#assets' },
        { label: t('help.cmms.toc_wo'),     href: '/portal/help/cmms#work-orders' },
        { label: t('help.cmms.toc_pm'),     href: '/portal/help/cmms#pm-schedules' },
        { label: t('help.cmms.toc_team'),   href: '/portal/help/cmms#team' },
      ],
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 border border-primary-100">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-primary-600 rounded-xl flex items-center justify-center">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('help.helpCenter')}</h1>
            <p className="text-gray-500 text-sm">{t('help.subtitle')}</p>
          </div>
        </div>
      </div>

      {/* Guide cards */}
      <div className="grid md:grid-cols-2 gap-6">
        {guides.map(guide => {
          const Icon = guide.icon;
          return (
            <div key={guide.href} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary-600" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">{guide.title}</h2>
              </div>
              <p className="text-sm text-gray-600 mb-4">{guide.description}</p>
              <ul className="space-y-1.5 mb-5">
                {guide.links.map(link => (
                  <li key={link.href}>
                    <Link
                      to={link.href}
                      className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1.5"
                    >
                      <ArrowRight className="w-3.5 h-3.5 flex-shrink-0" />
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to={guide.href} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700">
                {t('help.viewFullGuide')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          );
        })}
      </div>

      {/* Still need help */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex items-start gap-4">
        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
          <HelpCircle className="w-5 h-5 text-amber-600" />
        </div>
        <div>
          <h3 className="font-semibold text-gray-900 mb-1">{t('help.stillNeedHelp')}</h3>
          <p className="text-sm text-gray-600 mb-3">{t('help.stillNeedHelpDesc')}</p>
          <Link to="/portal/support" className="inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors">
            {t('help.openSupport')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;
